<template>
    <base-dialog :value="dialog" :width="480" :title="$t('calendar_library.save_title')" :loading="loading"
        @input="val => { if (!val) hide() }" @close="hide">
        <div class="stl">
            <div class="stl-row">
                <span class="stl-label">{{ $t('calendar_library.save_as') }}</span>
                <span class="stl-chip">{{ typeLabel }}</span>
            </div>
            <kimsa-text-field :label="$t('common.name')" dense force-value :value="name" @change="name = $event"
                @keyup.enter.native="save" />
            <kimsa-combobox v-if="type !== 'prescriptions'" :label="$t('common.folder')" dense :items="folderOptions"
                item-text="label" :value="folder" @change="folder = $event" ref="folders" />
            <div class="stl-label">{{ $t('calendar_library.content') }}</div>
            <div class="stl-preview">
                <template v-for="(group, i) in preview">
                    <div v-if="group.title" :key="`t${i}`" class="stl-preview-block">{{ group.title }}</div>
                    <div v-for="(row, j) in group.rows" :key="`r${i}-${j}`" class="stl-preview-row">
                        <span>{{ row.name }}</span>
                        <span>{{ row.meta }}</span>
                    </div>
                </template>
            </div>
        </div>
        <template v-slot:footer>
            <dialog-actions :confirm-label="$t('common.save')" :disabled="!name" :loading="loading" @cancel="hide"
                @confirm="save" />
        </template>
    </base-dialog>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { db, Timestamp } from '@/utils/Firebase'
import { clearBlocksAssignedRoutine, exerciseSetsLabel, getPayloadBaseRoutine, getPayloadBlock } from '@/utils/Exercises'
import BaseDialog from '@/components/common/BaseDialog'
import DialogActions from '@/components/common/DialogActions'
import KimsaTextField from '@/components/common/inputs/KimsaTextField'
import KimsaCombobox from '@/components/common/inputs/KimsaCombobox'

export default {
    name: 'SaveToLibraryDialog',
    components: { BaseDialog, DialogActions, KimsaTextField, KimsaCombobox },
    props: {
        coachId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            dialog: false,
            loading: false,
            type: null,
            source: null,
            name: '',
            folder: null,
        }
    },
    computed: {
        ...mapState(['folders', 'globalFolders', 'categoriesOptions']),
        typeLabel(vm = this) {
            if (vm.type === 'prescriptions') return vm.$t('common.prescription')
            if (vm.type === 'blocks') return vm.$tc('common.blocks', 1)
            return vm.$t('common.routine')
        },
        folderOptions(vm = this) {
            return [...vm.globalFolders, ...vm.folders]
                .filter(folder => folder.type === vm.type && !folder.isCircuit)
                .map(folder => ({ ...folder, label: vm.folderLabel(folder) }))
        },
        blocks(vm = this) {
            if (vm.type === 'routines') return vm.source?.blocks || []
            if (vm.type === 'blocks') return vm.source ? [vm.source] : []
            return []
        },
        preview(vm = this) {
            if (vm.type === 'prescriptions') {
                return [{ title: null, rows: [{ name: vm.source?.name || '', meta: exerciseSetsLabel(vm.source, null, vm.$t) }] }]
            }
            return vm.blocks.map((block, i) => ({
                title: vm.type === 'routines' ? (block.name || `${vm.$tc('common.blocks', 1)} ${i + 1}`) : null,
                rows: (block.exercises || []).map(exercise => ({
                    name: exercise.name,
                    meta: exerciseSetsLabel(exercise, block, vm.$t),
                })),
            }))
        },
    },
    async mounted() {
        const vm = this
        if (vm.coachId && vm.folders.length === 0) await vm.bindFolders(vm.coachId)
        if (vm.globalFolders.length === 0) await vm.bindGlobalFolders()
        if (!vm.categoriesOptions || vm.categoriesOptions.length === 0) await vm.fetchExerciseCategories()
    },
    methods: {
        ...mapActions(['bindFolders', 'bindGlobalFolders', 'fetchExerciseCategories']),
        show(type, source, name) {
            const vm = this
            vm.type = type
            vm.source = source
            vm.name = name || ''
            vm.folder = null
            if (vm.$refs.folders) vm.$refs.folders.model = null
            vm.dialog = true
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.type = null
            vm.source = null
            vm.name = ''
            vm.folder = null
        },
        prescriptionPayload() {
            const vm = this
            const exercise = vm.source || {}
            const categoryName = exercise.categories?.[0] || exercise.category || null
            const category = (vm.categoriesOptions || []).find(item => vm.categoryKey(item.name) === vm.categoryKey(categoryName)) || null
            return {
                name: vm.name,
                commentary: '',
                config: JSON.parse(JSON.stringify(exercise.config || {})),
                weekConfigs: [],
                eachSide: !!exercise.eachSide,
                painScale: !!exercise.painScale,
                weightMode: exercise.weightMode || null,
                hasTime: !!exercise.time,
                time: exercise.time || null,
                coachId: vm.coachId,
                category: category?.id || null,
                categoryName: category?.name || categoryName || null,
                tags: [],
                weeks: 1,
                used: 0,
            }
        },
        buildPayload() {
            const vm = this
            if (vm.type === 'prescriptions') return vm.prescriptionPayload()
            const folder = vm.folder?.id ? vm.folder : { id: null, name: null }
            if (vm.type === 'blocks') return { ...getPayloadBlock(vm.name, vm.coachId, vm.source, folder), used: 0 }
            const routine = { ...vm.source, coachId: vm.coachId }
            const blocks = clearBlocksAssignedRoutine(vm.source?.blocks || [])
            const objective = vm.source?.description?.objective || ''
            return { ...getPayloadBaseRoutine(vm.name, routine, blocks, objective, null, []), folder: folder.id, used: 0, created_at: Timestamp.now() }
        },
        async save() {
            const vm = this
            if (!vm.name || vm.loading) return
            await vm.withLoading(async () => {
                const payload = vm.buildPayload()
                const ref = await db.collection(vm.type).add(payload)
                vm.$emit('saved', { type: vm.type, id: ref.id, name: vm.name, payload })
                vm.hide()
            })
        },
    },
}
</script>

<style scoped>
.stl {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.stl-row {
    display: flex;
    align-items: center;
    gap: 10px;
}

.stl-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .06em;
    text-transform: uppercase;
    color: var(--v-txtMuted-base);
}

.stl-chip {
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    color: var(--v-accent-base);
    background: rgba(51, 153, 153, .12);
}

.theme--dark .stl-chip {
    color: var(--v-primary-base);
    background: rgba(204, 255, 51, .1);
}

.stl-preview {
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-height: 170px;
    overflow-y: auto;
    padding: 8px 12px;
    border-radius: 8px;
    border: 1px solid var(--v-outline2-base);
    background: var(--v-surface2-base);
    font-size: 12px;
    color: var(--v-txtSecondary-base);
}

.stl-preview-row {
    display: flex;
    justify-content: space-between;
    gap: 10px;
}

.stl-preview-row span:last-child {
    white-space: nowrap;
    color: var(--v-txtMuted-base);
}

.stl-preview-block {
    margin-top: 4px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .08em;
    text-transform: uppercase;
    color: var(--v-txtMuted-base);
}

.stl-preview-block:first-child {
    margin-top: 0;
}
</style>
